
import React, { useState } from 'react';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useTeacherAvailabilityExceptions } from '@/hooks/useTeacherAvailabilityExceptions';
import { TeacherAvailabilityException, TIME_SLOTS } from '@/types/teacherAvailability';

interface ExceptionsManagerProps {
  teacherCode: string;
}

export function ExceptionsManager({ teacherCode }: ExceptionsManagerProps) {
  const { exceptions, isLoading, addException, updateException, deleteException } = useTeacherAvailabilityExceptions(teacherCode);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingException, setEditingException] = useState<TeacherAvailabilityException | null>(null);
  const [formData, setFormData] = useState({
    exceptionDate: '',
    exceptionType: 'NO_WORK' as 'NO_WORK' | 'PARTIAL_UNAVAILABLE',
    startTime: '',
    endTime: '',
    reason: ''
  });

  const resetForm = () => {
    setFormData({
      exceptionDate: '',
      exceptionType: 'NO_WORK',
      startTime: '',
      endTime: '',
      reason: ''
    });
    setEditingException(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const exceptionData = {
      exceptionDate: formData.exceptionDate,
      exceptionType: formData.exceptionType,
      startTime: formData.exceptionType === 'PARTIAL_UNAVAILABLE' ? formData.startTime : undefined,
      endTime: formData.exceptionType === 'PARTIAL_UNAVAILABLE' ? formData.endTime : undefined,
      reason: formData.reason || undefined
    };

    let success = false;
    if (editingException) {
      success = await updateException(editingException.id, exceptionData);
    } else {
      success = await addException(exceptionData);
    }

    if (success) {
      setIsDialogOpen(false);
      resetForm();
    }
  };

  const handleEdit = (exception: TeacherAvailabilityException) => {
    setEditingException(exception);
    setFormData({
      exceptionDate: exception.exception_date,
      exceptionType: exception.exception_type,
      startTime: exception.start_time || '',
      endTime: exception.end_time || '',
      reason: exception.reason || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('คุณต้องการลบวันพิเศษนี้หรือไม่?')) {
      await deleteException(id);
    }
  };

  if (isLoading) {
    return <div className="text-center py-4">กำลังโหลด...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">วัน/เวลา หยุดการสอน (ชั่วคราว)</h3>
        
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="w-4 h-4 mr-2" />
              เพิ่มวันพิเศษ
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingException ? 'แก้ไขวันพิเศษ' : 'เพิ่มวันพิเศษ'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="exceptionDate">วันที่ *</Label>
                <Input
                  id="exceptionDate"
                  type="date"
                  value={formData.exceptionDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, exceptionDate: e.target.value }))}
                  required
                />
              </div>

              <div>
                <Label htmlFor="exceptionType">ประเภท *</Label>
                <Select
                  value={formData.exceptionType}
                  onValueChange={(value: 'NO_WORK' | 'PARTIAL_UNAVAILABLE') => 
                    setFormData(prev => ({ ...prev, exceptionType: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NO_WORK">ไม่รับสอนทั้งวัน</SelectItem>
                    <SelectItem value="PARTIAL_UNAVAILABLE">ไม่รับสอนบางช่วงเวลา</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.exceptionType === 'PARTIAL_UNAVAILABLE' && (
                <div className="flex space-x-2">
                  <div className="flex-1">
                    <Label htmlFor="startTime">เวลาเริ่ม</Label>
                    <Select
                      value={formData.startTime}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, startTime: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="เลือกเวลา" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIME_SLOTS.map(time => (
                          <SelectItem key={time} value={time}>{time}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label htmlFor="endTime">เวลาสิ้นสุด</Label>
                    <Select
                      value={formData.endTime}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, endTime: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="เลือกเวลา" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIME_SLOTS.map(time => (
                          <SelectItem key={time} value={time}>{time}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="reason">เหตุผล</Label>
                <Textarea
                  id="reason"
                  value={formData.reason}
                  onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="ระบุเหตุผล (ถ้ามี)"
                  rows={3}
                />
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="flex-1"
                >
                  ยกเลิก
                </Button>
                <Button type="submit" className="flex-1">
                  {editingException ? 'อัพเดท' : 'เพิ่ม'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {exceptions.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">
          ยังไม่มีวันพิเศษ
        </Card>
      ) : (
        <div className="space-y-2">
          {exceptions.map((exception) => (
            <Card key={exception.id} className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">
                    {new Date(exception.exception_date).toLocaleDateString('th-TH')}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {exception.exception_type === 'NO_WORK' ? 'ไม่รับสอนทั้งวัน' : 
                     `ไม่รับสอน ${exception.start_time} - ${exception.end_time}`}
                  </div>
                  {exception.reason && (
                    <div className="text-sm text-muted-foreground mt-1">
                      เหตุผล: {exception.reason}
                    </div>
                  )}
                </div>
                <div className="flex space-x-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleEdit(exception)}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(exception.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
